import { Pipe, inject, type PipeTransform } from '@angular/core';
import { type Locale } from '@shared/models/auth';
import { ACTIVE_LOCALE } from '@tvd/cdk/locale';

@Pipe({ name: 'relativeTime', pure: false })
export class RelativeTimePipe implements PipeTransform {
  // Reactive so it follows the language picker (see ACTIVE_LOCALE provider).
  readonly #locale = inject(ACTIVE_LOCALE);

  #key = '';
  #result = '';

  transform(value: Date | string | null, unit?: Intl.RelativeTimeFormatUnit) {
    if (!value) return '';
    const locale = this.#locale();
    const stamp = value instanceof Date ? value.getTime() : value;
    const key = `${stamp}|${unit}|${locale}`;
    // Impure pipe so it reacts to the out-of-band locale change; memoise so
    // formatRelativeTime only re-runs when the value, unit or locale moves
    // (matching the previous pure-pipe behaviour — no live ticking).
    if (key === this.#key) return this.#result;
    this.#key = key;
    this.#result = formatRelativeTime({ value, locale, unit });
    return this.#result;
  }
}

const getUnit = (
  date: Date | string,
  unit?: Intl.RelativeTimeFormatUnit,
): { unit: Intl.RelativeTimeFormatUnit; diff: number } => {
  const now = Date.now();
  const diff = now - new Date(date).getTime();

  if (unit) {
    switch (unit) {
      case 'day':
        return { unit: 'day', diff: Math.floor(diff / 1000 / 60 / 60 / 24) };
      case 'hour':
        return { unit: 'hour', diff: Math.floor(diff / 1000 / 60 / 60) };
      case 'minute':
        return { unit: 'minute', diff: Math.floor(diff / 1000 / 60) };
      case 'second':
        return { unit: 'second', diff: Math.floor(diff / 1000) };
    }
  }

  const diffInSeconds = diff / 1000;
  const diffInMinutes = diffInSeconds / 60;
  const diffInHours = diffInMinutes / 60;

  if (diffInHours >= 1) {
    return { unit: 'hour', diff: Math.floor(diffInHours) };
  }
  if (diffInMinutes >= 1) {
    return { unit: 'minute', diff: Math.floor(diffInMinutes) };
  }

  return { unit: 'second', diff: Math.floor(diffInSeconds) };
};

export const formatRelativeTime = (opts: {
  value: Date | string;
  locale: Locale;
  unit?: Intl.RelativeTimeFormatUnit;
}) => {
  const { unit, diff } = getUnit(opts.value, opts.unit);
  const rtf = new Intl.RelativeTimeFormat(opts.locale, {
    numeric: 'auto',
  });
  return rtf.format(-diff, unit);
};
